'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { ONE_DAY, ONE_MINUTE } from "@/src/util/relativetime";
import { createNewSubscription, getSubscriptionIfExists, isPushPermissionGranted, syncSubscriptionToServer, unsubscribe as utilUnsubscribe } from "@/src/util/webpush";
import WebPushServiceWorker from "./serviceworker";

// keep track of last time we auto synced, only sync existing subscription
// to server if it's been more than a day since last time
const LOCAL_STORAGE_KEY_SUBSCRIPTION_TIME_MS = 'webPushSubscriptionTimeMs';

// keep track of whether user manually subscribed
// we use this to avoid auto-resubscribing if the user opted out
const LOCAL_STORAGE_KEY_DID_USER_SUBSCRIBE = 'webPushDidUserSubscribe';

type WebPushContextProps = {
    // did the user grant permission and subscribe?
    // this is null if we don't know yet
    isClientSubscribed: boolean | null;

    // is the subscription synced to server?
    // this is null if we don't know yet and only changes
    // when we sync to server
    isServerSynced: boolean | null;

    // returns true if any web push operation is in progress
    isLoading: boolean;

    // keeps track if a user manually subscribed
    // we use this to avoid auto-resubscribing if the user opted
    // out on the site but still has the browser's permission granted
    didUserSubscribe: boolean;

    // code to auto heal the subscription state
    // tries to resubscribe and sync to server if needed
    autoheal: (forceSyncExistingSubscription?: boolean) => Promise<void>;

    // subscribes the user and syncs the subscription to server
    subscribeAndSyncSubscriptionToServer: () => Promise<void>;

    // unsubscribes the user
    unsubscribe: () => Promise<void>;
};

const WebPushContext = createContext<WebPushContextProps | null>(null);

type Props = {
    children: React.ReactNode;
}

export default function WebPushContextProvider({ children }: Props) {
    const [isClientSubscribed, setIsClientSubscribed] = useState<boolean | null>(null);
    const [isServerSynced, setIsServerSynced] = useState<boolean | null>(null);

    // loading counter to track ongoing operations
    const [loadingCounter, setLoadingCounter] = useState<number>(0);

    // last time the user's subscription was synced to server
    const [webPushSubscriptionTimeMs, setWebPushSubscriptionTimeMs] = useLocalStorage<number>(LOCAL_STORAGE_KEY_SUBSCRIPTION_TIME_MS, 0);

    // true if the user pressed the "subscribe" button
    const [didUserSubscribe, setDidUserSubscribe] = useLocalStorage<boolean>(LOCAL_STORAGE_KEY_DID_USER_SUBSCRIBE, false);

    // wrapper for async operations to keep track if loading is in progress
    const withLoading = useCallback(
        async function <T>(fn: () => Promise<T>): Promise<T> {
            setLoadingCounter((count) => count + 1);

            try {
                return await fn();
            } finally {
                setLoadingCounter((count) => Math.max(0, count - 1));
            }
        },
        [],
    );

    const subscribeAndSyncSubscriptionToServer = useCallback(
        () => withLoading(
            async (): Promise<void> => {
                // ask since the user is manually subscribing
                const isGranted = await isPushPermissionGranted(true);

                // fail early if permission not granted
                if (!isGranted) {
                    setIsClientSubscribed(false);
                    setIsServerSynced(false);
                    return;
                }

                // user has granted permission, so count this as subscribing
                // that way if there's an error we can auto heal later
                setDidUserSubscribe(true);

                let subscription = await getSubscriptionIfExists();
                if (!subscription) {
                    subscription = await createNewSubscription();
                }

                setIsClientSubscribed(!!subscription);

                if (subscription) {
                    const didServerSync = await syncSubscriptionToServer(subscription);

                    setIsServerSynced(didServerSync);
                    setWebPushSubscriptionTimeMs(Date.now());
                } else {
                    setIsServerSynced(false);
                }

                return;
            },
        ),
        [
            setDidUserSubscribe,
            setWebPushSubscriptionTimeMs,
            withLoading,
        ],
    );

    const autoheal = useCallback(
        (forceSyncExistingSubscription: boolean = false) => withLoading(
            async (): Promise<void> => {
                // don't prompt since we're autohealing
                const isGranted = await isPushPermissionGranted(false);

                // fail early if permission not granted or user subscribed
                if (!isGranted) {
                    setIsClientSubscribed(false);
                    setIsServerSynced(false);
                    return;
                }

                // let's still sync to the server every day to be safe
                const shouldSyncExistingSubscriptionToServer = !webPushSubscriptionTimeMs
                    || (Date.now() - webPushSubscriptionTimeMs) > ONE_DAY;

                let subscription = await getSubscriptionIfExists();
                setIsClientSubscribed(!!subscription);

                if (!didUserSubscribe) {
                    // user didn't manually subscribe, so don't autoheal further
                    return;
                }

                if (subscription) {
                    if (
                        forceSyncExistingSubscription
                        || shouldSyncExistingSubscriptionToServer
                    ) {
                        const didServerSync = await syncSubscriptionToServer(subscription);

                        setIsServerSynced(didServerSync);
                        setWebPushSubscriptionTimeMs(Date.now());
                    }

                    return;
                }

                subscription = await createNewSubscription();
                setIsClientSubscribed(!!subscription);

                if (subscription) {
                    const didServerSync = await syncSubscriptionToServer(subscription);

                    setIsServerSynced(didServerSync);
                    setWebPushSubscriptionTimeMs(Date.now());
                } else {
                    setIsServerSynced(false);
                }
            }
        ),
        [
            didUserSubscribe,
            setWebPushSubscriptionTimeMs,
            webPushSubscriptionTimeMs,
            withLoading,
        ],
    );

    const unsubscribe = useCallback(
        () => withLoading(
            async (): Promise<void> => {
                const result = await utilUnsubscribe();

                if (result) {
                    setIsClientSubscribed(false);
                    setIsServerSynced(false);
                    setDidUserSubscribe(false);
                }
            }
        ),
        [
            setDidUserSubscribe,
            withLoading,
        ],
    );

    useEffect(
        () => {
            autoheal();

            const timeout = setInterval(autoheal, ONE_MINUTE);
            return () => clearInterval(timeout);
        },
        [autoheal],
    );

    const value = useMemo(
        (): WebPushContextProps => ({
            autoheal,
            didUserSubscribe,
            isClientSubscribed,
            isLoading: loadingCounter > 0,
            isServerSynced,
            subscribeAndSyncSubscriptionToServer,
            unsubscribe,
        }),
        [
            autoheal,
            didUserSubscribe,
            isClientSubscribed,
            isServerSynced,
            loadingCounter,
            subscribeAndSyncSubscriptionToServer,
            unsubscribe,
        ],
    );

    return (
        <WebPushContext.Provider value={value}>
            <WebPushServiceWorker />
            {children}
        </WebPushContext.Provider>
    );
}

export function useWebPushContext() {
    const ctx = useContext(WebPushContext);

    if (!ctx) {
        throw new Error('useWebPushContext must be used within a WebPushContextProvider');
    }

    return ctx;
}
